/**
 * MovementMap.jsx — Round 6-pre (2026-09).
 *
 * كانت الخريطة تحسب «قبل أسبوع» من بيانات اليوم نفسها بتاريخ مرجعي أقدم
 * (وهم حركة، انتقالات مستحيلة). الآن: تقرأ الحركة الحقيقية من
 * /api/segment-snapshots/movement — مقارنة آخر لقطتين أسبوعيتين محفوظتين
 * فعليًا فقط. صورة واحدة أو صفر ⇒ رسالة صادقة بلا أي رقم حركة.
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { getSegmentMeta } from "../lib/segmentation";
import { useSegmentsStatus } from "../lib/useSegmentsStatus";
import CustomerListModal from "../components/CustomerListModal";
import { formatKSADate } from "../lib/ksaTime";

// 5A-final (سياق البند 3) — حالة التشغيل عربية دوماً، لا "ready"/"failed" خامة.
const RUN_STATUS_LABELS = { ready: "جاهزة", failed: "فشلت", running: "قيد التشغيل" };

const METRICS_META = {
  upgrades: { title: "ارتقوا لشريحة أعلى", color: "#34C759" },
  downgrades: { title: "تراجعوا لشريحة أدنى", color: "#FF3B30" },
  stable: { title: "تغيّروا بلا ارتقاء أو تراجع (رتبة متطابقة في سلّم الولاء)", color: "#78716C" },
  new_went_higher: { title: "جدد ارتقوا", color: "#0A84FF" },
  new_became_drifter: { title: "جدد تحوّلوا لعابر", color: "#BF5AF2" },
  entered_risk: { title: "دخلوا شريحة خطر", color: "#FF9500" },
  time_passage: { title: "انتقالات بمرور الوقت", color: "#78716C" },
};

function segChip(code) {
  // A3 (البند 3ب) — تسمية/لون من تعريفات الخادم مباشرة (getSegmentMeta)؛
  // لا META محلي بعد اليوم.
  const d = getSegmentMeta(code);
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "4px 10px", borderRadius: 999,
      background: `${d.color}22`, color: d.color,
      fontSize: 12, fontWeight: 600, whiteSpace: "nowrap",
    }}>
      {d.label}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return formatKSADate(new Date(iso), {
      year: "numeric", month: "2-digit", day: "2-digit",
    });
  } catch (_e) { return String(iso); }
}

function NumberButton({ testId, value, onClick, color }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      style={{
        border: "none", cursor: "pointer", background: `${color}18`,
        color, borderRadius: 12, padding: "6px 14px",
        fontSize: 20, fontWeight: 800, lineHeight: 1,
        transition: "transform 120ms ease, background 120ms ease",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = `${color}30`; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = `${color}18`; }}
    >
      {Number(value || 0).toLocaleString("en-US")}
    </button>
  );
}

function triggerBadge(triggeredBy) {
  const isAuto = triggeredBy === "scheduler";
  return (
    <span style={{
      fontSize: 10, padding: "2px 8px", borderRadius: 999, fontWeight: 700,
      background: isAuto ? "#DCFCE7" : "#FEF3C7", color: isAuto ? "#166534" : "#92400E",
    }}>
      {isAuto ? "🔁 أسبوعية تلقائية" : triggeredBy === "bootstrap_on_deploy" ? "🚀 إقلاع أول" : "✋ يدوية"}
    </span>
  );
}

export default function MovementMap() {
  useSegmentsStatus(); // إعادة رسم بعد اكتمال تحميل تعريفات الشرائح (segChip)
  const [state, setState] = useState({ loading: true, err: null, data: null });
  const [runs, setRuns] = useState([]);
  const [running, setRunning] = useState(false);
  const [showRuns, setShowRuns] = useState(false);
  // جولة تصحيح 2026-10-13 (البند 5ب) — «مسترجعون من الفقد»/«فُقدوا هذا
  // الأسبوع» يفتحان CustomerListModal الحقيقي (kind جديد في customers_
  // unified_list.py)، لا نافذة drilldown الخفيفة أعلاه — طلب صريح.
  // ④ C3 — كل رقم في الخريطة (المؤشرات + أزواج الانتقال) يفتح نفس
  // CustomerListModal: {kind, segmentCode, title}.
  const [listKind, setListKind] = useState(null);
  const auth = useAuth();
  const role = (auth?.user?.role || "").toLowerCase();
  const canRunNow = role === "admin" || role === "ceo";

  const loadMovement = useCallback(() => {
    setState((s) => ({ ...s, loading: true, err: null }));
    api.get("/segment-snapshots/movement")
      .then((r) => setState({ loading: false, err: null, data: r.data }))
      .catch((e) => {
        const msg = e?.response?.data?.detail || e?.message || "خطأ";
        setState({ loading: false, err: msg, data: null });
      });
  }, []);

  const loadRuns = useCallback(() => {
    api.get("/segment-snapshots/runs?limit=50")
      .then((r) => setRuns(r.data?.items || []))
      .catch(() => setRuns([]));
  }, []);

  useEffect(() => { loadMovement(); loadRuns(); }, [loadMovement, loadRuns]);

  const runNow = () => {
    if (running) return;
    setRunning(true);
    api.post("/segment-snapshots/run-now")
      .then(() => { loadMovement(); loadRuns(); })
      .catch((e) => {
        const msg = e?.response?.data?.detail || e?.message || "تعذّر أخذ اللقطة";
        setState((s) => ({ ...s, err: msg }));
      })
      .finally(() => setRunning(false));
  };

  const openDrill = (metric) => setListKind({
    kind: "movement", segmentCode: metric, title: METRICS_META[metric]?.title || metric,
  });
  const openPair = (m, tp) => setListKind({
    kind: "movement", segmentCode: `${tp ? "tp:" : ""}${m.from}->${m.to}`,
    title: `${getSegmentMeta(m.from).label} ← ${getSegmentMeta(m.to).label}`,
  });

  const data = state.data || {};
  const isReady = data.status === "ready";
  const isInsufficient = data.status === "insufficient";
  const isIncompatible = data.status === "incompatible_versions";
  const headline = data.headline || {};
  const movementsFull = Array.isArray(data.movements) ? data.movements : [];
  const topMovements = Array.isArray(data.top_movements) ? data.top_movements : [];
  const topTimePassageMovements = Array.isArray(data.top_time_passage_movements) ? data.top_time_passage_movements : [];
  // تصحيح A3 (البند 3أ) — كان totalMoved يُجمَع من top_movements (أعلى 10
  // أزواج فقط)، فيصغر عن ارتقاء+تراجع الكاملين (167+84=251 على الإنتاج
  // مقابل 213 معروضة هنا) كلما تجاوزت الأزواج المميّزة (from→to) عشرة.
  // الآن من headline.behavioral_transitions_total (upgrades+downgrades+
  // stable من نفس حلقة الخادم بالضبط — راجع compute_movement) مع fallback
  // لجمع data.movements الكامل إن غاب الحقل (توافق خلفي). اختبار معادلة
  // مخصَّص: test_a3_weekly_movement_cards_equal_list_total.py.
  const totalMoved = headline.behavioral_transitions_total != null
    ? Number(headline.behavioral_transitions_total)
    : movementsFull.reduce((s, m) => s + Number(m.count || 0), 0);

  const latestRun = runs.find((r) => r.status === "ready") || runs[0];
  const measuredPopulation = isReady ? data.customers_compared : (latestRun?.customers_written ?? null);

  // 5A-final (البند 3) — سطر واحد لكل تاريخ لقطة (لا سطر لكل ضغطة يدوية).
  // كل تشغيل يبقى مسجَّلاً في segment_snapshot_runs بالخلفية؛ هذا تجميع عرض فقط.
  const groupedRuns = useMemo(() => {
    const byDate = new Map();
    const undated = [];
    for (const r of runs) {
      if (!r.snapshot_date) { undated.push(r); continue; }
      const g = byDate.get(r.snapshot_date) || {
        snapshot_date: r.snapshot_date, run_count: 0, latest: r,
      };
      g.run_count += 1;
      const latestTs = g.latest.finished_at || g.latest.started_at || "";
      const curTs = r.finished_at || r.started_at || "";
      if (curTs > latestTs) g.latest = r;
      byDate.set(r.snapshot_date, g);
    }
    const dated = Array.from(byDate.values())
      .sort((a, b) => b.snapshot_date.localeCompare(a.snapshot_date));
    return { dated, undated };
  }, [runs]);

  return (
    <div
      dir="rtl"
      data-testid="movement-map-root"
      style={{
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Noto Naskh Arabic', sans-serif",
        background: "#FFFFFF", width: "100%", boxSizing: "border-box",
        borderRadius: 20, padding: "28px 24px", color: "#3D2A1C",
        boxShadow: "0 8px 28px rgba(61,42,28,0.10)",
        border: "1px solid rgba(61,42,28,0.12)",
        maxWidth: 760, marginTop: 24,
      }}
    >
      <header style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between",
                        alignItems: "flex-start", gap: 16, marginBottom: 20 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: -0.5 }}>
            خريطة الحركة الأسبوعية
          </div>
          <div data-testid="movement-map-pair-used" style={{ fontSize: 13, color: "#78716C", marginTop: 4 }}>
            {isReady
              ? `مقارنة ${fmtDate(data.prev_date)} ← ${fmtDate(data.latest_date)} · لقطتان أسبوعيتان تلقائيتان`
              : "قياس حقيقي يعتمد على لقطتين أسبوعيتين تلقائيتين محفوظتين"}
            {/* تصحيح A3 (البند 3ب) — وسم صريح إن أُعيدت إحدى اللقطتين
                بالمنهجية الحالية بعد أن كانت محسوبة بمنهجية أقدم. */}
            {isReady && (data.prev_recomputed || data.latest_recomputed) && (
              <span data-testid="movement-map-recomputed-badge" style={{
                marginInlineStart: 8, fontSize: 10, padding: "2px 8px", borderRadius: 999,
                background: "#FEF3C7", color: "#92400E", fontWeight: 700,
              }}>
                ↻ أُعيد حسابها بالمنهجية الحالية
                {data.prev_recomputed && !data.latest_recomputed ? ` (${fmtDate(data.prev_date)})` : ""}
                {data.latest_recomputed && !data.prev_recomputed ? ` (${fmtDate(data.latest_date)})` : ""}
              </span>
            )}
          </div>
          <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
            <span style={{
              fontSize: 10, padding: "2px 8px", borderRadius: 999,
              background: "#F3E8FF", color: "#6B21A8", fontWeight: 700,
            }}>
              🎯 المصدر: segment_snapshots (لقطة أسبوعية حقيقية)
            </span>
            {/* جولة تصحيح 2026-10-14 (المرحلة 2، البند 11) — نسخة تعريف
                الشرائح الفعلية تحت العنوان مباشرة، لا في رسالة الخطأ فقط،
                حتى في حالة النجاح العادية. */}
            {isReady && Array.isArray(data.classifier_version) && data.classifier_version.length > 0 && (
              <span data-testid="movement-map-classifier-version" style={{
                fontSize: 10, padding: "2px 8px", borderRadius: 999,
                background: "#EFF6FF", color: "#1D4ED8", fontWeight: 700,
              }}>
                📐 نسخة تعريف الشرائح: {data.classifier_version.join("، ")}
              </span>
            )}
            {isIncompatible && (
              <span data-testid="movement-map-classifier-version-mismatch" style={{
                fontSize: 10, padding: "2px 8px", borderRadius: 999,
                background: "#FEF3F2", color: "#B91C1C", fontWeight: 700,
              }}>
                📐 {fmtDate(data.prev_date)}: نسخة {(data.prev_classifier_version || []).join("، ") || "—"} ·
                {" "}{fmtDate(data.latest_date)}: نسخة {(data.latest_classifier_version || []).join("، ") || "—"}
              </span>
            )}
          </div>
          {canRunNow && (
            <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 10 }}>
              <button
                type="button"
                data-testid="movement-map-run-now-btn"
                onClick={runNow}
                disabled={running}
                style={{
                  border: "none", cursor: running ? "wait" : "pointer",
                  padding: "8px 18px", borderRadius: 999,
                  background: running ? "#A8A29E" : "#5856D6",
                  color: "#fff", fontSize: 13, fontWeight: 700,
                  boxShadow: "0 2px 8px rgba(88,86,214,0.30)",
                  transition: "background 150ms ease",
                }}
              >
                {running ? "⏳ جارٍ أخذ اللقطة…" : "📸 أخذ لقطة الآن"}
              </button>
              <button
                type="button"
                data-testid="movement-map-toggle-runs-btn"
                onClick={() => setShowRuns((v) => !v)}
                style={{
                  border: "1px solid rgba(61,42,28,0.18)", cursor: "pointer",
                  padding: "8px 18px", borderRadius: 999, background: "transparent",
                  color: "#3D2A1C", fontSize: 13, fontWeight: 700,
                }}
              >
                🔍 سجل التشغيل
              </button>
            </div>
          )}
        </div>
        <div data-testid="movement-map-population" style={{
          background: "rgba(61,42,28,0.06)",
          borderRadius: 14, padding: "10px 16px", textAlign: "center", minWidth: 110,
        }}>
          <div style={{ fontSize: measuredPopulation != null ? 22 : 13, fontWeight: 700, color: "#3D2A1C" }}>
            {measuredPopulation != null ? measuredPopulation.toLocaleString("en-US") : "—"}
          </div>
          <div style={{ fontSize: 11, color: "#78716C", marginTop: 2 }}>
            عميل في مجتمع القياس
          </div>
          <div data-testid="movement-map-population-note" style={{ fontSize: 9.5, color: "#A8A29E", marginTop: 4, maxWidth: 140, lineHeight: 1.5 }}>
            تقاطع اللقطتين فقط (لا اتحاد) — عميل غير موجود في كلتيهما لا يُحتسَب هنا، لذا يصغر عن إجمالي «الخريطة الزمنية» أدناه.
          </div>
        </div>
      </header>

      {canRunNow && showRuns && (
        <div data-testid="movement-map-runs-log" style={{
          marginBottom: 18, padding: 14, borderRadius: 12,
          background: "rgba(61,42,28,0.04)", fontSize: 12,
        }}>
          {runs.length === 0 && <div style={{ color: "#78716C" }}>لا يوجد سجل تشغيل بعد.</div>}
          {groupedRuns.dated.map((g) => {
            const r = g.latest;
            const lastAt = r.finished_at || r.started_at || "—";
            return (
              <div key={g.snapshot_date} data-testid={`movement-map-run-${g.snapshot_date}`}
                   style={{ display: "flex", flexWrap: "wrap", gap: 10, padding: "6px 0",
                            borderBottom: "1px solid rgba(61,42,28,0.08)" }}>
                <span>{r.status === "ready" ? "✅" : r.status === "failed" ? "❌" : "⏳"} {RUN_STATUS_LABELS[r.status] || r.status}</span>
                <span>· تاريخ اللقطة: {g.snapshot_date}</span>
                {triggerBadge(r.triggered_by)}
                <span>· عملاء: {r.customers_written ?? "—"}</span>
                <span>· مرات التشغيل: {g.run_count}</span>
                <span>· آخر تشغيل: {String(lastAt).replace("T", " ").slice(0, 16)}</span>
                {r.error && <span style={{ color: "#B91C1C" }}>· خطأ: {r.error}</span>}
              </div>
            );
          })}
          {groupedRuns.undated.map((r) => (
            <div key={r.id} data-testid={`movement-map-run-undated-${r.id}`}
                 style={{ display: "flex", flexWrap: "wrap", gap: 10, padding: "6px 0",
                          borderBottom: "1px solid rgba(61,42,28,0.08)" }}>
              <span>{r.status === "ready" ? "✅" : r.status === "failed" ? "❌" : "⏳"} {RUN_STATUS_LABELS[r.status] || r.status}</span>
              <span>· بلا تاريخ لقطة (لم يكتمل)</span>
              <span>· طريقة: {r.triggered_by}</span>
              {r.error && <span style={{ color: "#B91C1C" }}>· خطأ: {r.error}</span>}
            </div>
          ))}
        </div>
      )}

      {state.loading && (
        <div data-testid="movement-map-loading"
             style={{ padding: "40px 0", textAlign: "center", color: "#78716C" }}>
          جارٍ تحميل الحركة…
        </div>
      )}

      {state.err && !state.loading && (
        <div data-testid="movement-map-error" style={{
          padding: 20, borderRadius: 14, background: "#FEF3F2",
          border: "1px solid rgba(185,28,28,0.35)",
          color: "#B91C1C", fontSize: 14,
        }}>
          تعذّر تحميل الحركة: {state.err}
        </div>
      )}

      {!state.loading && !state.err && isIncompatible && (
        <div data-testid="movement-map-incompatible-versions" style={{
          padding: "26px 20px", borderRadius: 14, background: "#FEF3F2",
          border: "1px solid rgba(185,28,28,0.25)", textAlign: "center",
        }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#B91C1C" }}>
            {data.message || "تغيّر تعريف الشرائح بين اللقطتين"}
          </div>
          <div data-testid="movement-map-incompatible-detail" style={{ fontSize: 12, color: "#78716C", marginTop: 6 }}>
            اللقطة {fmtDate(data.prev_date)} بنسخة تعريف مختلفة عن {fmtDate(data.latest_date)} —
            لا تُقارَنان حتى لا تظهر حركة وهمية بين تعريفين مختلفين.
          </div>
        </div>
      )}

      {!state.loading && !state.err && isInsufficient && (
        <div data-testid="movement-map-insufficient" style={{
          padding: "26px 20px", borderRadius: 14, background: "#FEF9EE",
          border: "1px solid rgba(217,150,0,0.25)", textAlign: "center",
        }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#92400E" }}>
            القياس الحقيقي يبدأ من {data.expected_second_snapshot_date}
          </div>
          <div data-testid="movement-map-insufficient-detail" style={{ fontSize: 12, color: "#78716C", marginTop: 6 }}>
            {(data.weekly_auto_count || 0)} لقطة أسبوعية تلقائية محفوظة حتى الآن
            {data.snapshots_count > (data.weekly_auto_count || 0)
              ? ` (من إجمالي ${data.snapshots_count} لقطة، منها لقطات يدوية)` : ""}
            {" "}— الحركة تُحسب فقط بمقارنة لقطتين أسبوعيتين تلقائيتين متتاليتين.
          </div>
        </div>
      )}

      {!state.loading && !state.err && isReady && (
        <>
          {/* الثلاث مؤشرات الرئيسية — كل رقم يفتح قائمة عملائه */}
          {/* جولة تصحيح 2026-10-13 (البند 5ب، ملاحظة تصميم من testing_agent) —
             minmax 160 بدل 200 كي تتّسع البطاقة الرابعة (الاسترجاع والفقد)
             في نفس الصف بجانب صافي الولاء/احتفاظ الجدد/التسرّب على الشاشات
             الواسعة، لا أن تسقط صفاً منفصلاً. */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                        gap: 12, marginBottom: 22 }}>
            <div data-testid="metric-card-net-loyalty" style={{
              padding: 16, borderRadius: 14, background: "rgba(61,42,28,0.04)",
              border: "1px solid rgba(61,42,28,0.10)",
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>⬆️ صافي الولاء</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <NumberButton testId="metric-upgrades" value={headline.net_loyalty?.upgrades}
                              color={METRICS_META.upgrades.color} onClick={() => openDrill("upgrades")} />
                <span style={{ color: "#A8A29E" }}>/</span>
                <NumberButton testId="metric-downgrades" value={headline.net_loyalty?.downgrades}
                              color={METRICS_META.downgrades.color} onClick={() => openDrill("downgrades")} />
                {/* تصحيح A3 (البند 3أ) — «ثبات» (sp≠sn برتبة متطابقة — ممكن
                    فقط مع سلّم ولاء مخصَّص بلا رتب فريدة). يظهر فقط حين
                    يكون > 0 كي لا يُزحم العرض للسلّم الافتراضي الشائع حيث
                    هو صفر دوماً؛ معادلة ارتقاء+تراجع+ثبات = القائمة محفوظة
                    في الخادم دوماً بصرف النظر عن ظهوره هنا. */}
                {Number(headline.net_loyalty?.stable || 0) > 0 && (
                  <>
                    <span style={{ color: "#A8A29E" }}>/</span>
                    <NumberButton testId="metric-stable" value={headline.net_loyalty?.stable}
                                  color={METRICS_META.stable.color} onClick={() => openDrill("stable")} />
                  </>
                )}
              </div>
              <div style={{ fontSize: 11, color: "#78716C", marginTop: 8 }}>
                الصافي: <b style={{ color: "#3D2A1C" }}>
                  {(headline.net_loyalty?.net || 0) >= 0 ? "+" : ""}{headline.net_loyalty?.net || 0}
                </b> · ⬆ ارتقاء / ⬇ تراجع
                {Number(headline.net_loyalty?.stable || 0) > 0 ? " / ⏺ ثبات" : ""}
              </div>
            </div>

            <div data-testid="metric-card-new-retention" style={{
              padding: 16, borderRadius: 14, background: "rgba(61,42,28,0.04)",
              border: "1px solid rgba(61,42,28,0.10)",
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>🆕 احتفاظ الجدد</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <NumberButton testId="metric-new-went-higher" value={headline.new_retention?.went_higher}
                              color={METRICS_META.new_went_higher.color}
                              onClick={() => openDrill("new_went_higher")} />
                <span style={{ color: "#A8A29E" }}>/</span>
                <NumberButton testId="metric-new-became-drifter" value={headline.new_retention?.became_drifter}
                              color={METRICS_META.new_became_drifter.color}
                              onClick={() => openDrill("new_became_drifter")} />
              </div>
              <div style={{ fontSize: 11, color: "#78716C", marginTop: 8 }}>
                من {headline.new_retention?.new_prev_total || 0} عميل «جديد» في اللقطة السابقة
              </div>
            </div>

            <div data-testid="metric-card-churn" style={{
              padding: 16, borderRadius: 14, background: "rgba(61,42,28,0.04)",
              border: "1px solid rgba(61,42,28,0.10)",
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>🚨 التسرّب</div>
              <NumberButton testId="metric-entered-risk" value={headline.churn?.entered_risk}
                            color={METRICS_META.entered_risk.color}
                            onClick={() => openDrill("entered_risk")} />
              <div style={{ fontSize: 11, color: "#78716C", marginTop: 8 }}>
                عملاء دخلوا شريحة مصنَّفة «خطر»
              </div>
            </div>

            {/* جولة تصحيح 2026-10-13 (البند 5ب) — بطاقة جديدة بجانب صافي
               الولاء والتسرّب. الرقمان يفتحان CustomerListModal الحقيقي
               (نفس مصدر بقية المنصة)، لا نافذة الـdrilldown الخفيفة أعلاه. */}
            <div data-testid="metric-card-recovery" style={{
              padding: 16, borderRadius: 14, background: "rgba(61,42,28,0.04)",
              border: "1px solid rgba(61,42,28,0.10)",
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>🔄 الاسترجاع والفقد</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <NumberButton testId="metric-recovered-from-loss"
                              value={headline.recovery?.recovered_from_loss}
                              color="#15803D" onClick={() => setListKind({ kind: "recovered_from_loss", title: "مسترجعون من الفقد" })} />
                <span style={{ color: "#A8A29E" }}>/</span>
                <NumberButton testId="metric-lost-this-week"
                              value={headline.recovery?.lost_this_week}
                              color="#B91C1C" onClick={() => setListKind({ kind: "lost_this_week", title: "فُقدوا هذا الأسبوع" })} />
              </div>
              <div style={{ fontSize: 11, color: "#78716C", marginTop: 8 }}>
                مسترجعون من الفقد / فُقدوا هذا الأسبوع
              </div>
            </div>
          </div>


          <div data-testid="movement-map-total" style={{ fontSize: 12, color: "#78716C", marginBottom: 10 }}>
            إجمالي الانتقالات: <b style={{ color: "#3D2A1C" }}>{totalMoved.toLocaleString("en-US")}</b>
          </div>

          {topMovements.length === 0 ? (
            <div data-testid="movement-map-empty"
                 style={{ padding: 20, color: "#78716C", textAlign: "center" }}>
              لا توجد انتقالات فعلية بين اللقطتين.
            </div>
          ) : (
            <ul data-testid="movement-map-list"
                style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 10 }}>
              {topMovements.map((m, idx) => (
                <li key={idx} data-testid={`movement-row-${idx}`}
                    style={{
                      display: "grid", gridTemplateColumns: "auto 1fr auto auto",
                      alignItems: "center", gap: 12,
                      padding: "14px 16px", background: "rgba(61,42,28,0.06)",
                      borderRadius: 14, border: "1px solid rgba(61,42,28,0.12)",
                    }}>
                  {segChip(m.from)}
                  <div aria-hidden style={{
                    textAlign: "center", color: "#A8A29E", fontSize: 18, lineHeight: 1,
                  }}>←</div>
                  {segChip(m.to)}
                  <NumberButton testId={`movement-count-${idx}`} value={m.count}
                                color="#3D2A1C" onClick={() => openPair(m, false)} />
                </li>
              ))}
            </ul>
          )}

          {Number(data.time_passage_transitions || 0) > 0 && (
            <details data-testid="movement-map-time-passage-section" style={{ marginTop: 18 }}>
              <summary style={{ cursor: "pointer", fontSize: 12.5, fontWeight: 700, color: "#78716C" }}>
                ⏳ انتقالات بمرور الوقت فقط (بلا زيارة جديدة) — {Number(data.time_passage_transitions || 0).toLocaleString("en-US")}
                <button type="button" data-testid="movement-map-time-passage-drilldown-btn"
                        onClick={(e) => { e.preventDefault(); openDrill("time_passage"); }}
                        style={{ marginInlineStart: 10, border: "none", background: "rgba(107,114,128,0.12)",
                                 color: "#57534E", borderRadius: 999, padding: "2px 10px", fontSize: 11, cursor: "pointer" }}>
                  عرض العملاء
                </button>
              </summary>
              <div style={{ fontSize: 11, color: "#A8A29E", margin: "8px 0" }}>
                تمايز recency طبيعي بلا فاتورة مدفوعة جديدة بين اللقطتين — مُستبعدة من صافي الولاء/التسرّب/احتفاظ الجدد أعلاه.
              </div>
              <ul data-testid="movement-map-time-passage-list"
                  style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}>
                {topTimePassageMovements.map((m, idx) => (
                  <li key={idx} data-testid={`movement-time-passage-row-${idx}`}
                      style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
                               background: "rgba(107,114,128,0.06)", borderRadius: 12 }}>
                    {segChip(m.from)}
                    <span aria-hidden style={{ color: "#A8A29E" }}>←</span>
                    {segChip(m.to)}
                    <span style={{ marginInlineStart: "auto" }}>
                      <NumberButton testId={`movement-time-passage-count-${idx}`} value={m.count}
                                    color="#57534E" onClick={() => openPair(m, true)} />
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}

      {listKind && (
        <CustomerListModal
          kind={listKind.kind}
          segmentCode={listKind.segmentCode || null}
          title={listKind.title}
          onClose={() => setListKind(null)}
        />
      )}

      <footer style={{ marginTop: 18, fontSize: 11, color: "#A8A29E", textAlign: "center" }}>
        يقرأ من /api/segment-snapshots/movement
      </footer>
    </div>
  );
}
